import { RichTextEditor, Link } from "@mantine/tiptap";
import { useEditor } from "@tiptap/react";
import Highlight from "@tiptap/extension-highlight";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import TextAlign from "@tiptap/extension-text-align";
import Superscript from "@tiptap/extension-superscript";
import SubScript from "@tiptap/extension-subscript";
import "@mantine/tiptap/styles.css";
import { Button, Flex, Text, Tooltip } from "@mantine/core";
import { IconBrandTelegram } from "@tabler/icons-react";
import { NotificationExtension } from "../../../../../../_base/extension/NotificationExtension";
import { UseFormReturnType } from "@mantine/form";
import {
  tblPromotion,
  tblPromotionCommand,
  tblPromotionDetailModel,
} from "../../../../../../model/TblPromotion";
import { useEffect, useRef, useState } from "react";

export const PromotionalDescriptionV2 = ({
  formHeader,
  handleAtt3,
  dataPromotionDetail,
  onDescriptionChange,
}: {
  formHeader?: UseFormReturnType<
    tblPromotionCommand,
    (values: tblPromotionCommand) => tblPromotionCommand
  >;
  handleAtt3?: string;
  dataPromotionDetail?: {
    data?: tblPromotionDetailModel | undefined;
    method?: string;
    dataDetail?: tblPromotion;
  };
  onDescriptionChange?: (
    moTa: string | null,
    moTaTemp: string | null,
    moTaConfirmed: boolean
  ) => void;
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link,
      Superscript,
      SubScript,
      Highlight,
      TextStyle,
      Color,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content:
      (dataPromotionDetail as any)?.method === "DETAIL"
        ? (dataPromotionDetail as any)?.data?.moTa
        : (dataPromotionDetail as any)?.method === "EDIT"
        ? (dataPromotionDetail as any)?.dataDetail?.tblPromotionCommand?.moTa
        : "",
    editable: dataPromotionDetail?.method === "DETAIL" ? false : true,
  });

  const handleSubmit = () => {
    if (!editor) return;

    const htmlContent = editor.getHTML();

    if (htmlContent.length > 4000) {
      NotificationExtension.Warn(
        "Vượt quá giới hạn 4000 ký tự. Vui lòng rút gọn nội dung."
      );
      return;
    }

    console.log("Xác nhận nội dung - set vào form:", htmlContent);
    NotificationExtension.Success("Gửi nội dung thành công.");
    (formHeader as any)?.setFieldValue("moTa", String(htmlContent));
    (formHeader as any)?.setFieldValue("moTaTemp", String(htmlContent));
    (formHeader as any)?.setFieldValue("moTaConfirmed", true);
    
    // Gọi callback để update state ở component cha
    if (onDescriptionChange) {
      onDescriptionChange(String(htmlContent), String(htmlContent), true);
    }
    
    console.log("Form values sau khi set:", {
      moTa: (formHeader as any)?.getValues?.()?.moTa,
      moTaTemp: (formHeader as any)?.getValues?.()?.moTaTemp,
      moTaConfirmed: (formHeader as any)?.getValues?.()?.moTaConfirmed,
    });
  };

  useEffect(() => {
    if (!editor) return;

    const contentToSet =
      (dataPromotionDetail as any)?.dataDetail?.tblPromotionCommand?.moTa ||
      (dataPromotionDetail as any)?.data?.moTa;

    if (contentToSet) {
      editor.commands.setContent(contentToSet);
    }
  }, [
    editor,
    (dataPromotionDetail as any)?.dataDetail?.tblPromotionCommand?.moTa,
    (dataPromotionDetail as any)?.data?.moTa,
  ]);

  const [moTaTempLocal, setMoTaTempLocal] = useState<string>(
    (formHeader as any)?.getValues?.()?.moTaTemp ?? ""
  );
  const debounceRef = useRef<number | null>(null);

  // Track editor updates but debounce writes to form to avoid excessive re-renders
  useEffect(() => {
    if (!editor) return;

    const updateHandler = () => {
      const html = editor.getHTML();
      setMoTaTempLocal(html);

      // debounce updating the form values to reduce parent re-renders
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
      debounceRef.current = window.setTimeout(() => {
        (formHeader as any)?.setFieldValue("moTaTemp", String(html));

        const confirmed = (formHeader as any)?.getValues?.()?.moTa;
        const isConfirmed = confirmed && confirmed === html;
        
        if (confirmed && confirmed !== html) {
          (formHeader as any)?.setFieldValue("moTaConfirmed", false);
        }
        
        // Gọi callback để update state ở component cha
        if (onDescriptionChange) {
          onDescriptionChange(
            confirmed || null,
            String(html),
            !!isConfirmed
          );
        }
      }, 800) as unknown as number;
    };

    editor.on("update", updateHandler);
    return () => {
      editor.off("update", updateHandler);
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
    };
  }, [editor, formHeader]);

  // If loading existing content (EDIT/DETAIL), set confirmed flag
  useEffect(() => {
    const contentToSet =
      (dataPromotionDetail as any)?.dataDetail?.tblPromotionCommand?.moTa ||
      (dataPromotionDetail as any)?.data?.moTa;
    if (contentToSet) {
      (formHeader as any)?.setFieldValue("moTa", String(contentToSet));
      (formHeader as any)?.setFieldValue("moTaConfirmed", true);
      (formHeader as any)?.setFieldValue("moTaTemp", String(contentToSet));
    }
  }, [dataPromotionDetail, formHeader]);

  return (
    <>
      <RichTextEditor
        editor={editor}
        style={{
          minHeight: 600,
          overflow: "visible",
          maxHeight: "none",
        }}
      >
        <RichTextEditor.Toolbar sticky stickyOffset={70}>
          <Flex
            justify="space-between"
            wrap={"wrap"}
            gap={10}
            align="center"
            w={"100%"}
          >
            <Flex gap="md" align="center">
              <RichTextEditor.ColorPicker
                colors={[
                  "#25262b",
                  "#868e96",
                  "#fa5252",
                  "#e64980",
                  "#be4bdb",
                  "#7950f2",
                  "#4c6ef5",
                  "#228be6",
                  "#15aabf",
                  "#12b886",
                  "#40c057",
                  "#82c91e",
                  "#fab005",
                  "#fd7e14",
                ]}
              />
              <RichTextEditor.ControlsGroup>
                <RichTextEditor.Bold />
                <RichTextEditor.Italic />
                <RichTextEditor.Underline />
                <RichTextEditor.Strikethrough />
                <RichTextEditor.ClearFormatting />
                <RichTextEditor.Highlight />
                <RichTextEditor.Code />
              </RichTextEditor.ControlsGroup>

              <RichTextEditor.ControlsGroup>
                <RichTextEditor.H3 />
                <RichTextEditor.H4 />
              </RichTextEditor.ControlsGroup>

              <RichTextEditor.ControlsGroup>
                <RichTextEditor.Blockquote />
                <RichTextEditor.Hr />
                <RichTextEditor.BulletList />
                <RichTextEditor.OrderedList />
                <RichTextEditor.Subscript />
                <RichTextEditor.Superscript />
              </RichTextEditor.ControlsGroup>

              <RichTextEditor.ControlsGroup>
                <RichTextEditor.Link />
              </RichTextEditor.ControlsGroup>

              <RichTextEditor.ControlsGroup>
                <RichTextEditor.AlignLeft />
                <RichTextEditor.AlignCenter />
                <RichTextEditor.AlignJustify />
                <RichTextEditor.AlignRight />
              </RichTextEditor.ControlsGroup>

              <RichTextEditor.ControlsGroup>
                <RichTextEditor.Undo />
                <RichTextEditor.Redo />
              </RichTextEditor.ControlsGroup>
            </Flex>
            {dataPromotionDetail?.method !== "DETAIL" && (
              <Tooltip label={"Tiến hành gửi nội dung"}>
                <Button
                  color="#1289f8"
                  variant="light"
                  bd={"1px solid #1289f8"}
                  leftSection={<IconBrandTelegram size={14} />}
                  onClick={handleSubmit}
                  disabled={dataPromotionDetail?.method === "DETAIL"}
                >
                  Xác nhận nôi dung
                </Button>
              </Tooltip>
            )}
          </Flex>
        </RichTextEditor.Toolbar>
        <RichTextEditor.Content />

        <Text
          size="sm"
          mt="xs"
          p={5}
          pos={"absolute"}
          bottom={0}
          left={0}
          color={(editor?.getHTML()?.length ?? 0) > 4000 ? "red" : "dimmed"}
        >
          {editor?.getHTML()?.length ?? 0}/4000 ký tự
        </Text>
      </RichTextEditor>
    </>
  );
};
